import React, { useEffect, useState } from 'react';

import { TextInputField, TextAreaField } from '@repo/ui/Form';
import Layout from '@/features/auth/layouts/LoginLayout';
import { TypeOf, z } from 'zod';
import { registerInput, registerToggle, useZodForm } from '@repo/hooks/useZodForm';
import { Button } from '@repo/ui/Button';
import { Form, SelectorInputField } from '@repo/ui/Form';
import { Toggle } from '@repo/ui/Toggle';
import { Skeleton } from '@repo/ui/Skeletons';
import { RadioInputField } from '@repo/ui/Form';
import { ToggleCheckboxInputField } from '@repo/ui/Form';
import { Card } from '@repo/ui/Card';
import { DataCard } from '@repo/ui/Card';
import { QuickLinks } from '@repo/ui/QuickLinks';
import { QuickLinkType } from '@repo/ui/QuickLinks';
import { Toast } from '@repo/ui/Toast';

import { TabNav } from '@repo/ui/Tabs';
import { NavTab } from '@repo/ui/Tabs';
import { CRUDForm } from '@repo/ui/Form';
import { CRUD, UseCRUDRead } from '@repo/util/CRUD';
import { CRUDDeleteButton } from '@repo/ui/Form';
import { CRUDRefetchButton } from '@repo/ui/Form';

const crudSchema = z.object({
  a: z.string().default(''),
  b: z.boolean().default(false),
});

type Defined<T> = T extends null | undefined ? never : T;

type CrudObject = Defined<UseCRUDRead<TypeOf<typeof crudSchema>>['data']> & {
  id: string;
  lastUpdate: number;
};

let exampleItems: CrudObject[] = [
  { id: 'abcd', a: 'something', b: false, lastUpdate: Date.now() },
  { id: 'efgh', a: 'other thing', b: true, lastUpdate: Date.now() },
];

const dummyLoadExampleItem = async (itemId: string) => {
  await new Promise((resolve) => setTimeout(resolve, (500 + Math.random() * 5000) | 0));
  const item = exampleItems.find((item) => item.id === itemId);
  if (!item) {
    throw new Error('404');
  }
  return item;
};

const exampleCRUD: CRUD<typeof crudSchema, CrudObject> = {
  async create(client, input) {
    const id = Math.random().toString(16);
    exampleItems.push({
      ...input,
      lastUpdate: Date.now(),
      id,
    });
    return id;
  },
  async delete(client, itemId) {
    await new Promise((resolve) => setTimeout(resolve, (500 + Math.random() * 5000) | 0));
    exampleItems = exampleItems.filter((item) => item.id === itemId);
  },
  async update(client, itemId, input) {
    exampleItems = exampleItems.filter((item) => item.id !== itemId);
    exampleItems.push({
      id: itemId,
      lastUpdate: Date.now(),
      ...input,
    });
  },
  useRead(itemId: string): UseCRUDRead<CrudObject> {
    const [data, setData] = useState<CrudObject>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error>();
    useEffect(() => {
      dummyLoadExampleItem(itemId)
        .then(
          (data) => setData(data),
          (err) => setError(err)
        )
        .finally(() => {
          setLoading(false);
        });
    }, [itemId]);
    return {
      data,
      loading,
      error,
      async refetch() {
        setLoading(true);
        try {
          const newData = await dummyLoadExampleItem(itemId);
          setData(newData);
          setError(undefined);
        } catch (err) {
          setData(undefined);
          setError(err as Error);
        } finally {
          setLoading(false);
        }
      },
    };
  },
};

const SimpleCrudForm = ({
  itemId,
  crud,
}: {
  itemId?: string | undefined;
  crud: CRUD<typeof crudSchema, CrudObject>;
}) => (
  <CRUDForm crud={crud} schema={crudSchema} itemId={itemId} className="my-10">
    {(form, data) => (
      <>
        <Form.Loading>Loading...</Form.Loading>
        <Form.Error autoClose={false}>{(error) => <>Error: {error}</>}</Form.Error>
        {data ? (
          <>
            <div>
              <em>ID: </em>
              {data.id}({data.lastUpdate})
            </div>
            <div>
              <em>Previous Value: </em>
              {data?.a} ({data?.b ? 'OK' : 'NOT'})
            </div>
          </>
        ) : null}
        <TextInputField {...registerInput(form, 'a')} />
        <Toggle {...registerToggle(form, 'b')} />
        <div className="flex flex-row gap-2">
          <Button type="submit">Submit</Button>
          <Button type="reset" variant="secondary">
            Reset
          </Button>
          <CRUDDeleteButton variant="danger">Delete</CRUDDeleteButton>
          <CRUDRefetchButton variant="tertiary">Refetch</CRUDRefetchButton>
        </div>
      </>
    )}
  </CRUDForm>
);

export const ComponentsPage = () => {
  const TabNavTabs: NavTab[] = [
    {
      title: 'tab 1',
      href: '/components',
    },
    {
      title: 'tab 2',
      href: '/components#tab2',
    },
    {
      title: 'tab 3',
      href: '/components#tab3',
    },
    {
      title: 'tab 4',
      href: '/components#tab4',
    },
  ];

  const QuickLinksSample: QuickLinkType[] = [
    {
      title: 'SMS',
      description: 'View your message details.',
      icon: 'SMS',
      href: 'messaging/smses',
    },
    {
      title: 'Numbers',
      description: 'View all your numbers.',
      icon: 'tag',
      href: '/numbers',
    },
  ];

  const form = useZodForm({
    defaultValues: async () => {
      await new Promise((resolve) => setTimeout(resolve, 2500));
      return {
        radio: 'B',
        'one-row': 'prefilled',
        togglePreselected: true,
        selector: 'z',
        'input-desc': '',
        'selector-2': 'x',
        lowercase: '',
        input: '',
        password: '',
        uppercase: '',
        trimspaces: '',
        'show-count': '',
        loading: '',
      } as const;
    },
    schema: z.object({
      normal: z.string().optional(),
      'one-row': z.string().optional(),
      loading: z.string().optional(),
      'show-count': z.string().optional(),
      'show-segments': z.string().optional(),
      'show-count-and-seg': z.string().optional(),
      toggle: z.boolean().optional(),
      togglePreselected: z.boolean(),
      radio: z.enum(['A', 'B', 'C']),
      selector: z.enum(['x', 'y', 'z']),
      'selector-2': z.enum(['x', 'y', 'z']),
      toggleIn: z.boolean().optional(),
      toggleInLabel: z.boolean().optional(),
      input: z.string(),
      'input-desc': z.string(),
      uppercase: z.string(),
      lowercase: z.string(),
      trimspaces: z.string(),
      password: z.string(),
    }),
    async onSubmit() {
      await new Promise((resolve) => setTimeout(resolve, 2500));
    },
  });

  const [openToast, setOpenToast] = useState<boolean>(false);
  return (
    <Layout>
      <Form {...form}>
        <Form.Debug />
        <Skeleton />
        <Skeleton count={3} />

        <TextInputField
          label="Label with popover"
          description="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum."
        />
        <TextAreaField label="Normal" {...registerInput(form, 'normal')} />
        <TextAreaField label="One row" {...registerInput(form, 'one-row')} rows={1} />
        <TextAreaField label="Loading" loading {...registerInput(form, 'loading')} />
        <TextAreaField
          label="Loading one Row"
          loading
          {...registerInput(form, 'loading')}
          rows={1}
        />
        <TextAreaField
          label="Show Character Count"
          showCharacterCount
          maxLength={20}
          {...registerInput(form, 'show-count')}
        />
        <TextAreaField
          label="Show Character Count"
          showSegmentCount
          segmentLength={10}
          {...registerInput(form, 'show-segments')}
        />
        <TextAreaField
          label="Show Character Count"
          showCharacterCount
          showSegmentCount
          segmentLength={10}
          maxLength={40}
          {...registerInput(form, 'show-count-and-seg')}
        />
        <TextInputField label="Text Input" {...registerInput(form, 'input')} />
        <TextInputField
          label="Input with description"
          description="Let's have more text here."
          {...registerInput(form, 'input-desc')}
        />
        <TextInputField
          label="Uppercase"
          casing="uppercase"
          {...registerInput(form, 'uppercase')}
        />
        <TextInputField
          label="Lowercase"
          casing="lowercase"
          {...registerInput(form, 'lowercase')}
        />
        <TextInputField label="Trim Spaces" trimSpaces {...registerInput(form, 'trimspaces')} />
        <TextInputField label="Loading" loading />
        <TextInputField type="password" label="Password" {...registerInput(form, 'password')} />
        <Toggle {...registerToggle(form, 'toggle')} />
        <Toggle {...registerToggle(form, 'togglePreselected')} />
        <RadioInputField label="A" {...registerInput(form, 'radio')} value="A" />
        <RadioInputField label="B" {...registerInput(form, 'radio')} value="B" />
        <RadioInputField label="C" {...registerInput(form, 'radio')} value="C" />
        <SelectorInputField
          label="Selector"
          options={[
            { label: 'X', value: 'x' },
            { label: 'Y', value: 'y' },
            { label: 'Z', value: 'z' },
          ]}
          {...registerInput(form, 'selector')}
        />
        <SelectorInputField
          label="Selector"
          disabled
          options={[
            { label: 'X', value: 'x' },
            { label: 'Y', value: 'y' },
            { label: 'Z', value: 'z' },
          ]}
          {...registerInput(form, 'selector')}
        />
        <SelectorInputField
          label="Selector 2"
          value="y"
          options={[
            { label: 'X', value: 'x' },
            { label: 'Y', value: 'y' },
            { label: 'Z', value: 'z' },
          ]}
          {...registerInput(form, 'selector-2')}
        />
        <ToggleCheckboxInputField {...registerToggle(form, 'toggleIn')} />
        <ToggleCheckboxInputField {...registerToggle(form, 'toggleInLabel')} label="Hello" />
        <ToggleCheckboxInputField
          {...registerToggle(form, 'toggleInLabel')}
          label="Hello"
          loading
        />
        <Card title="Card" />
        <DataCard
          title="DataCard"
          data={[
            {
              columnCount: 3,
              groupData: [
                { entry: 'hi', label: 'ho' },
                { entry: 'x', label: 'y' },
                { entry: 'z', label: 'Z' },
              ],
              groupTitle: 'XYZ',
            },
          ]}
        />
        <Button type="submit">Submit</Button>
        <Button type="reset">Reset</Button>
      </Form>

      <div className="my-10">
        <Card title="Quicklinks">
          <QuickLinks links={QuickLinksSample} />
        </Card>
      </div>

      <div className="my-10">
        <TabNav tabs={TabNavTabs}>Some Content</TabNav>
      </div>

      <div className="my-10">
        <Button onClick={() => setOpenToast(true)}>Open persistant toast</Button>
        <Toast
          open={openToast}
          title="You have unsaved changes"
          variant="warning"
          fade="left"
          position="bottom-0 right-0"
          icon="edit_note"
          fixed
        />
      </div>

      <SimpleCrudForm crud={exampleCRUD} />
      <SimpleCrudForm crud={exampleCRUD} itemId="abcd" />
      <SimpleCrudForm crud={exampleCRUD} itemId="efgh" />
      <SimpleCrudForm crud={exampleCRUD} itemId="wxyz" />
    </Layout>
  );
};
